/**
 * Owner: Masa Bayu
 * File: dropcap-paint.ts
 * Date:Time: 2026-10-14 04:02
 * Description: L4 paintDropCapFromTip · tip pair only · no remasure · no decide.
 */

import { paintTextFromTip } from './text-paint.js';
import type {
  DropCapFlowLinePlan,
  DropCapPaintInput,
  DropCapPaintPlan,
  TextAlign,
  TipTextElement,
} from './types.js';

function resolveAlign(el: TipTextElement): TextAlign {
  return el.style.textAlign;
}

/**
 * Passive dropcap paint from tip index.
 * wrapInset / wrapLineCount / column* from tip · CSS textAlign from tip lines.
 * MUST NOT call justifyDecision / Face remasure / canvas measure.
 */
export function paintDropCapFromTip(
  input: DropCapPaintInput,
): DropCapPaintPlan | null {
  const { pair, elementsById, page } = input;
  const letterEl = elementsById.get(pair.letterId);
  const hostEl = elementsById.get(pair.hostId);
  if (!letterEl || !hostEl) {
    return null;
  }

  const letterPaint = paintTextFromTip({
    element: letterEl,
    page,
  });

  const flowLines: DropCapFlowLinePlan[] = [];
  for (let i = 0; i < pair.flowIds.length; i += 1) {
    const id = pair.flowIds[i]!;
    const el = elementsById.get(id);
    if (!el) continue;

    const wrap = i < pair.wrapLineCount;
    const marginLeft = wrap ? pair.wrapInset : 0;
    const width = Math.max(1, pair.columnWidth - marginLeft);
    const textAlign = resolveAlign(el);

    const line: DropCapFlowLinePlan = {
      elementIds: [id],
      content: el.content,
      marginLeft,
      width,
      textAlign,
      top: el.bbox.y,
      height: el.bbox.height,
      fontFamily: el.style.fontFamily,
      fontSize: el.style.fontSize,
      dataTextAlign: textAlign,
    };

    if (textAlign === 'justify') {
      flowLines.push({ ...line, textAlignLast: 'justify' });
    } else {
      flowLines.push(line);
    }
  }

  if (flowLines.length === 0) {
    return null;
  }

  return {
    box: {
      left: pair.columnLeft,
      top: pair.top,
      width: pair.columnWidth,
    },
    letter: {
      elementId: pair.letterId,
      content: letterEl.content,
      style: letterPaint,
    },
    flowLines,
    dataAttrs: {
      'data-native-dropcap': 'true',
      'data-render-pipe': 'v1',
      'data-letter-id': pair.letterId,
      'data-host-id': pair.hostId,
    },
  };
}
